import {startCompletion} from "@codemirror/autocomplete";
import {history, indentLess, indentMore} from '@codemirror/commands';
import {Compartment, EditorSelection, EditorState} from '@codemirror/state';
import {EditorView, placeholder} from '@codemirror/view';

import {addOrderedList, addUnorderedList} from './commands/addList.js';
import {addTable} from "./commands/addTable";
import {appendBlockQuote} from "./commands/appendBlockQuote.js";
import {appendUserMention} from "./commands/appendUserMention.js";
import {clearContent} from './commands/changes.js';
import {formatBold, formatItalics, formatStrikeThrough, formatUnderline} from './commands/format.js';
import {addImage, insertImage} from "./commands/images.js";
import {insertBlockQuote} from "./commands/insertBlockQuote.js";
import {insertCodeBlock} from './commands/insertCodeBlock.js';
import {insertEmoji} from "./commands/insertEmoji.js";
import {insertKeyNotation} from "./commands/insertKeyNotation.js";
import {addLink, insertLink} from './commands/links.js';
import {highlightDecorations} from './decoration/decorations.js';
import autocomplete from './extensions/autocomplete.js';
import {emojiPreview} from "./extensions/emojiPreview.js";
import styleExtension from './extensions/highlight.js';
import flavouredMarkdown from './extensions/markdown.js';
import {pasteExtension} from './extensions/paste/extension.js';
import {imagePreview} from "./extensions/preview.js";
import keymaps from './extensions/shortcuts.js';
import {availableCommands} from './queries.js';

import "./style.scss";

const theme = new Compartment();

export class Editor4Play {
  constructor(
    parent,
    _placeholder,
    initialValue,
    {onChange, onSubmit, onCancel, onStateChange},
    usernamesSource,
    noLanguageLabel,
    codeBlockLanguages,
    emojiImageUrl,
    emojis,
    emoticonsSectionName,
    usersSectionName,
    languageSectionName,
    shortEmoticonSectionName,
    shortEmoticons,
    darkTheme,
  ) {
    const extensions = [
      history({newGroupDelay: 300}),
      keymaps(onSubmit, onCancel),
      placeholder(_placeholder),
      flavouredMarkdown,
      highlightDecorations(),
      EditorView.lineWrapping,
      EditorView.contentAttributes.of({
        spellcheck: 'true',
        autocorrect: 'on',
        autocapitalize: 'on',
      }),
      styleExtension,
      EditorView.baseTheme({
        '&.cm-editor.cm-focused': {
          outline: 'none',
        },
        '&light .highlight-code-block': {
          'background-color': '#f2f2f2',
        },
        '&dark .highlight-code-block': {
          'background-color': '#2f2f2f',
        },
        '&light .highlight-quote': {
          color: '#777',
          'background-color': '#f7f7f7',
          'border-color': '#ddd',
        },
        '&dark .highlight-quote': {
          color: '#777',
          'background-color': '#202020',
          'border-color': '#2f2f2f',
        },
        '&light .highlight-quote + .cm-line-image-preview': {
          'border-color': '#ddd',
        },
        '&dark .highlight-quote + .cm-line-image-preview': {
          'border-color': '#2f2f2f',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete': {
          'background-color': '#101010',
          'color': '#eee',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete ul li.cm-completionOption': {
          'background-color': 'black',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete ul li.cm-completionOption:hover': {
          'background-color': '#202020',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete ul li.cm-completionOption .cm-completionLabel': {
          color: '#acacac',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete ul li.cm-completionOption .cm-completionLabel .cm-completionMatchedText': {
          color: 'white',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete ul li.cm-completionOption[aria-selected]': {
          'background-color': '#303030',
        },
        '&dark .cm-tooltip.cm-tooltip-autocomplete ul li.cm-completionOption[aria-selected]:hover': {
          'background-color': '#383838',
        },
      }),
      theme.of(EditorView.darkTheme.of(darkTheme)),
      autocomplete(
        usernamesSource,
        noLanguageLabel,
        codeBlockLanguages,
        emojis,
        emojiImageUrl,
        emoticonsSectionName,
        usersSectionName,
        languageSectionName,
        shortEmoticonSectionName,
        shortEmoticons,
      ),
      pasteExtension(),
      imagePreview(),
      emojiPreview(emojiImageUrl),
    ];
    const selection = EditorSelection.single(initialValue.replaceAll('\r', '').length);
    const state = EditorState.create({doc: initialValue, selection, extensions});
    onStateChange(availableCommands({state}));
    const dispatch = transaction => {
      view.update([transaction]);
      if (transaction.docChanged) {
        onChange && onChange(transaction.newDoc.toString());
      }
      if (transaction.docChanged || transaction.selection) {
        onStateChange(availableCommands(view));
      }
    };
    const view = new EditorView({parent, state, dispatch});
    this.view = view;
  }

  /*
   * The methods below are only supposed to be called by controls,
   * that is mouse/touch actions, and thus need to apply focus back
   * to the editor. If the actions need to be triggered by shortcut,
   * or by some other action from within the component, the editor
   * should apply them from within, where re-focus isn't necessary.
   */

  changeTheme(dark) {
    this.view.dispatch({
      effects: theme.reconfigure(EditorView.darkTheme.of(dark)),
    })
  }

  putBold() {
    formatBold(this.view);
    this.view.focus();
  }

  putItalics() {
    formatItalics(this.view);
    this.view.focus();
  }

  putUnderline() {
    formatUnderline(this.view);
    this.view.focus();
  }

  indentMore() {
    indentMore(this.view);
    this.view.focus();
  }

  indentLess() {
    indentLess(this.view);
    this.view.focus();
  }

  putStrikeThrough() {
    formatStrikeThrough(this.view);
    this.view.focus();
  }

  putLink(placeholder) {
    addLink(placeholder)(this.view);
    this.view.focus();
  }

  putImage(placeholder) {
    addImage(placeholder)(this.view);
    this.view.focus();
  }

  insertImage(href, title) {
    insertImage(title, href)(this.view);
    this.view.focus();
  }

  insertLink(href, title) {
    insertLink(title, href)(this.view);
    this.view.focus();
  }

  putBlockQuote(placeholder) {
    insertBlockQuote(placeholder)(this.view);
    this.view.focus();
  }

  appendBlockQuote(content) {
    appendBlockQuote(content)(this.view);
    this.view.focus();
  }

  appendUserMention(username) {
    appendUserMention(username)(this.view);
    this.view.focus();
  }

  putCodeBlock() {
    insertCodeBlock(this.view);
    startCompletion(this.view);
    this.view.focus();
  }

  putListUnordered(placeholder) {
    addUnorderedList(placeholder)(this.view);
    this.view.focus();
  }

  putListOrdered(placeholder) {
    addOrderedList(placeholder)(this.view);
    this.view.focus();
  }

  putTable(headerByIndex, firstCell) {
    addTable(headerByIndex, firstCell)(this.view);
    this.view.focus();
  }

  putKey(key) {
    insertKeyNotation(key)(this.view);
    this.view.focus();
  }

  insertEmoji(emojiName) {
    insertEmoji(emojiName)(this.view);
    this.view.focus();
  }

  clear() {
    // This only clears the content, doesn't reset the
    // editor, so the history and configuration remains
    clearContent(this.view);
  }

  focus() {
    this.view.focus();
  }

  destroy() {
    this.view.destroy();
  }
}
